import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';

import * as UI_SCHEMAS from './StepConfigUISchemas';
import {StepTypeConfigUISchema} from './StepConfigUISchemas';

interface StepConfigSettings<StepType extends PipelineStepType, StepVersion extends number> {
  /**
   * The UI Schema determines client-side behavior that should be customized per-step-type.
   */
  uiSchema: StepTypeConfigUISchema<StepType, StepVersion>;
}

export const STEP_CONFIG_SETTINGS = {
  [PipelineStepType.AGGREGATE_DATASET]: [{uiSchema: UI_SCHEMAS.AGGREGATE_DATASET_V0_UI_SCHEMA}],
  [PipelineStepType.APPLY_COLUMN_MAPPING]: [{uiSchema: UI_SCHEMAS.APPLY_COLUMN_MAPPING_V0_UI_SCHEMA}],
  [PipelineStepType.BIN_DATA]: [{uiSchema: UI_SCHEMAS.BIN_DATA_V0_UI_SCHEMA}],
  [PipelineStepType.COMPUTED_COLUMN]: [{uiSchema: UI_SCHEMAS.COMPUTED_COLUMN_V0_UI_SCHEMA}],
  [PipelineStepType.CONVERT_ASM_TO_CSV]: [{uiSchema: UI_SCHEMAS.CONVERT_ASM_TO_CSV_V0_UI_SCHEMA}],
  [PipelineStepType.CONVERT_JSON_TO_DATASET]: [{uiSchema: UI_SCHEMAS.CONVERT_JSON_TO_DATASET_V0_UI_SCHEMA}],
  [PipelineStepType.CONVERT_TO_ASM]: [{uiSchema: UI_SCHEMAS.CONVERT_TO_ASM_V0_UI_SCHEMA}],
  [PipelineStepType.CREATE_DATASET_FROM_FILE]: [{uiSchema: UI_SCHEMAS.CREATE_DATASET_FROM_FILE_V0_UI_SCHEMA}],
  [PipelineStepType.CREATE_PROCEDURE_DATASET]: [{uiSchema: UI_SCHEMAS.CREATE_PROCEDURE_DATASET_V0_UI_SCHEMA}],
  [PipelineStepType.EXTRACT_OR_REMOVE_LINES]: [{uiSchema: UI_SCHEMAS.EXTRACT_OR_REMOVE_LINES_V0_UI_SCHEMA}],
  [PipelineStepType.FILTER_DATASET]: [{uiSchema: UI_SCHEMAS.FILTER_DATASET_V0_UI_SCHEMA}],
  [PipelineStepType.FIND_AND_REPLACE]: [{uiSchema: UI_SCHEMAS.FIND_AND_REPLACE_V0_UI_SCHEMA}],
  [PipelineStepType.HIDE_COLUMNS]: [{uiSchema: UI_SCHEMAS.HIDE_COLUMNS_V0_UI_SCHEMA}],
  [PipelineStepType.MAP_JSON_TO_DATASETS_AND_FILES]: [
    {uiSchema: UI_SCHEMAS.MAP_JSON_TO_DATASETS_AND_FILES_V0_UI_SCHEMA},
  ],
  [PipelineStepType.PIVOT_DATASET]: [{uiSchema: UI_SCHEMAS.PIVOT_DATASET_V0_UI_SCHEMA}],
  [PipelineStepType.REGRESSION_INTERPOLATION]: [{uiSchema: UI_SCHEMAS.REGRESSION_INTERPOLATION_V0_UI_SCHEMA}],
  [PipelineStepType.RENAME_COLUMNS]: [{uiSchema: UI_SCHEMAS.RENAME_COLUMNS_V0_UI_SCHEMA}],
  [PipelineStepType.REORDER_COLUMNS]: [{uiSchema: UI_SCHEMAS.REORDER_COLUMNS_V0_UI_SCHEMA}],
  [PipelineStepType.RUN_HIGHRES_PROTOCOL]: [{uiSchema: UI_SCHEMAS.RUN_HIGHRES_PROTOCOL_V0_UI_SCHEMA}],
  [PipelineStepType.SEND_TO_CONNECTION]: [{uiSchema: UI_SCHEMAS.SEND_TO_CONNECTION_V0_UI_SCHEMA}],
  [PipelineStepType.SORT_COLUMNS]: [{uiSchema: UI_SCHEMAS.SORT_COLUMNS_V0_UI_SCHEMA}],
  [PipelineStepType.RETYPE_COLUMNS]: [{uiSchema: UI_SCHEMAS.RETYPE_COLUMNS_V0_UI_SCHEMA}],
  [PipelineStepType.UNPIVOT_DATASET]: [{uiSchema: UI_SCHEMAS.UNPIVOT_DATASET_V0_UI_SCHEMA}],
  [PipelineStepType.USER_DEFINED_FUNCTION]: [{uiSchema: UI_SCHEMAS.USER_DEFINED_FUNCTION_V0_UI_SCHEMA}],
  [PipelineStepType.WINDOW_FUNCTIONS]: [{uiSchema: UI_SCHEMAS.WINDOW_FUNCTIONS_V0_UI_SCHEMA}],
} satisfies {
  [StepType in PipelineStepType]?: {
    [StepVersion in number]: StepConfigSettings<StepType, StepVersion>;
  };
};

export const STEP_CONFIG_SETTINGS_V2 = {
  [PipelineStepType.CHART_VIEW]: [
    {
      uiSchema: UI_SCHEMAS.CHART_VIEW_V0_UI_SCHEMA,
    },
  ],
  [PipelineStepType.CONNECTION_DATA_DATASET]: [{uiSchema: UI_SCHEMAS.CONNECTION_DATA_DATASET_V0_UI_SCHEMA}],
  [PipelineStepType.DATA_CATALOG_DATASET]: [{uiSchema: UI_SCHEMAS.DATA_CATALOG_DATASET_V0_UI_SCHEMA}],
  [PipelineStepType.DATA_CATALOG_FILE]: [{uiSchema: UI_SCHEMAS.DATA_CATALOG_FILE_V0_UI_SCHEMA}],
  [PipelineStepType.DATA_CATALOG_FILES]: [{uiSchema: UI_SCHEMAS.DATA_CATALOG_FILES_V0_UI_SCHEMA}],
  [PipelineStepType.CONVERT_FILE_TO_DATASET_WITH_DATA_ENTRY_ASSISTANT]: [
    {uiSchema: UI_SCHEMAS.CONVERT_FILE_TO_DATASET_WITH_DATA_ENTRY_ASSISTANT_V0_UI_SCHEMA},
  ],
  [PipelineStepType.CREATE_METERING_USAGE_CHART]: [
    {uiSchema: UI_SCHEMAS.CREATE_METERING_USAGE_CHART_V0_UI_SCHEMA},
  ],
  [PipelineStepType.EXTRACT_DATA_FRAME_VALUE]: [{uiSchema: UI_SCHEMAS.EXTRACT_DATA_FRAME_VALUE_V0_UI_SCHEMA}],
  [PipelineStepType.DEFINE_TEMPLATE_VARIABLES]: [
    {uiSchema: UI_SCHEMAS.DEFINE_TEMPLATE_VARIABLES_V0_UI_SCHEMA},
  ],
  [PipelineStepType.INITIALIZE_DATASET]: [
    // Versions 0 and 1 have config: null and no UI schema; only version 2 is registered.
    // stepTypeIsConfigEditorSupported filters out versions with undefined settings.
    undefined,
    undefined,
    {uiSchema: UI_SCHEMAS.INITIALIZE_DATASET_V2_UI_SCHEMA},
  ],
  [PipelineStepType.JOIN]: [{uiSchema: UI_SCHEMAS.JOIN_V0_UI_SCHEMA}],
  [PipelineStepType.LOOK_UP]: [{uiSchema: UI_SCHEMAS.LOOK_UP_V0_UI_SCHEMA}],
  [PipelineStepType.PLATE_SCHEMA_DATASET]: [{uiSchema: UI_SCHEMAS.PLATE_SCHEMA_DATASET_V0_UI_SCHEMA}],
  [PipelineStepType.REGISTRY_SCHEMA_DATASET]: [{uiSchema: UI_SCHEMAS.REGISTRY_SCHEMA_DATASET_V0_UI_SCHEMA}],
  [PipelineStepType.UNION_DATASETS]: [{uiSchema: UI_SCHEMAS.UNION_DATASETS_V0_UI_SCHEMA}],
  [PipelineStepType.UPLOAD_CSV_DATASET]: [{uiSchema: UI_SCHEMAS.UPLOAD_CSV_DATASET_V0_UI_SCHEMA}],
} satisfies Partial<Record<PipelineStepType, Array<{uiSchema: object} | undefined>>>;
